import React from 'react';
import {
  FlexRow,
  HashLength,
  TableData,
  TruncateBox,
  TableRow,
} from '@make-software/cspr-design';
import {
  Tooltip,
  BodyText,
  formatTimestamp,
  formatTimestampAge,
} from '@block-explorer/ui';
import {
  AccountInfoResult,
  MapNFTTokenStandardToName,
  NFTTokenMetadata,
  NFTTokenResult,
} from '@block-explorer/entities';
import { buildRouterPath } from '../../router/utils';
import { NFT_TOKEN_PATH } from '../../router/paths';
import { ContractIdentifierWithApi } from '../contract-identifier/contract-identifier';
import { HistoryLink } from '../history-link/history-link';
import CopyButton from '../copy-button/copy-button';
import TransactorInfo from '../transactor-info/transactor-info';
import { useTranslation } from 'react-i18next';

interface NFTTokensListItemProps {
  data: NFTTokenResult | null;
  loading: boolean;
  accountInfo?: AccountInfoResult | undefined;
  showOwnerInfo?: boolean;
  showTimestamp?: boolean;
  showContractPackageInfo?: boolean;
}

export const NFTTokensListItem = ({
  data,
  loading,
  accountInfo,
  showTimestamp = true,
  showContractPackageInfo = true,
  showOwnerInfo = false,
}: NFTTokensListItemProps) => {
  const { t } = useTranslation();
  const metadataKeyValue: NFTTokenMetadata = {
    ...data?.offchain_metadata,
    ...data?.onchain_metadata,
  };

  const nftTokenPath = !data
    ? ''
    : buildRouterPath(
        NFT_TOKEN_PATH,
        {
          contract_package_hash: data?.contract_package_hash,
          token_id: data?.token_id,
        },
        undefined
      );

  return (
    <TableRow>
      {showContractPackageInfo ? (
        <TableData>
          <ContractIdentifierWithApi
            contractPackage={data?.contract_package}
            hash={data?.contract_package_hash}
            hashLength={HashLength.TINY}
            loading={loading}
            withName
          />
        </TableData>
      ) : null}
      <TableData>
        <FlexRow itemsSpacing={4} align={'center'}>
          <Tooltip tooltipContent={data?.token_id} caption={t('Token ID')}>
            <TruncateBox size={1}>
              <HistoryLink
                href={nftTokenPath}
                ariaDescription={'Link to nft token details'}
              >
                <BodyText size={3}>{data?.token_id}</BodyText>
              </HistoryLink>
            </TruncateBox>
          </Tooltip>
          <CopyButton value={data?.token_id} />
        </FlexRow>
      </TableData>
      <TableData>
        <FlexRow align={'center'}>
          <TruncateBox size={1}>
            <BodyText size={3}>{metadataKeyValue?.name}</BodyText>
          </TruncateBox>
        </FlexRow>
      </TableData>
      <TableData>
        <FlexRow align={'center'}>
          <TruncateBox size={2}>
            <BodyText size={3} variation="darkGray" noWrap>
              {metadataKeyValue?.description}
            </BodyText>
          </TruncateBox>
        </FlexRow>
      </TableData>
      {showTimestamp ? (
        <TableData>
          <Tooltip tooltipContent={data && formatTimestamp(data.timestamp)}>
            <BodyText size={3} variation="darkGray" noWrap>
              {data && formatTimestampAge(data?.timestamp)}
            </BodyText>
          </Tooltip>
        </TableData>
      ) : null}
      {showOwnerInfo ? (
        <TableData>
          <TransactorInfo
            hashLength={HashLength.TINY}
            accountInfo={
              accountInfo?.account_info || accountInfo?.centralized_account_info
            }
            publicKey={data?.owner_public_key || data?.owner_hash}
            csprName={data?.owner_cspr_name}
            loading={!data && loading}
            hash={data?.owner_hash}
            type={data?.owner_type}
          />
        </TableData>
      ) : null}
      <TableData>
        <BodyText size={3}>
          {MapNFTTokenStandardToName[data?.token_standard_id || '']}
        </BodyText>
      </TableData>
    </TableRow>
  );
};

export default NFTTokensListItem;
